import { useEffect, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/admin/kit";
import { useTerms } from "@/lib/industry";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-3xl bg-white border p-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
    <p className="font-serif text-4xl mt-2">{value}</p>
  </div>
);

function Chart({ title, data, dataKey, testid }) {
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={testid}>
      <h3 className="font-serif text-xl mb-4">{title}</h3>
      {data.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No data yet</p> : (
        <ResponsiveContainer width="100%" height={Math.max(160, data.length * 42)}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
            <CartesianGrid horizontal={false} stroke="#eee" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey={dataKey} fill="var(--ba)" radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function BookingClicks({ b, t }) {
  const max = Math.max(1, ...b.tours.flatMap((x) => x.weeks));
  return (
    <div className="rounded-3xl bg-white border p-6 mt-6" data-testid="booking-clicks">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><h3 className="font-serif text-xl">Booking clicks per week</h3><p className="text-sm text-slate-500">Taps on your booking buttons, by {t.item}, over the last 8 weeks.</p></div>
        <p className="text-sm" data-testid="booking-clicks-last7"><span className="font-serif text-3xl mr-1">{b.last7}</span>in the last 7 days</p>
      </div>
      {b.tours.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No booking clicks yet. Add booking buttons under Branding & team.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="booking-clicks-table">
            <thead><tr className="text-xs text-slate-500"><th className="text-left font-medium py-2 pr-3">{t.item[0].toUpperCase() + t.item.slice(1)}</th>{b.weeks.map((w) => <th key={w} className="font-medium px-1 whitespace-nowrap">{w}</th>)}<th className="font-medium px-2">Total</th><th className="font-medium px-2 whitespace-nowrap">Click rate</th></tr></thead>
            <tbody>{b.tours.map((x) => (
              <tr key={x.id || "page"} className="border-t" data-testid={`booking-row-${x.id || "page"}`}>
                <td className="py-2 pr-3 font-medium whitespace-nowrap">{x.name}</td>
                {x.weeks.map((n, i) => <td key={i} className="px-1 py-1.5"><div className="h-8 min-w-[2.5rem] rounded-lg grid place-items-center text-xs font-semibold" style={{ background: n ? `color-mix(in srgb, var(--ba) ${15 + (n / max) * 70}%, white)` : "#f8fafc", color: n / max > 0.6 ? "#fff" : "#334155" }}>{n || "·"}</div></td>)}
                <td className="px-2 text-center font-semibold">{x.total}</td>
                <td className="px-2 text-center text-slate-500" title="Booking clicks ÷ tour views">{x.rate == null ? "—" : `${x.rate}%`}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  const { t, sells } = useTerms();
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const mins = `${Math.floor(d.avg_tour_seconds / 60)}m ${d.avg_tour_seconds % 60}s`;
  return (
    <div>
      <PageTitle help="analytics" title="Analytics" sub={`How ${t.who} are engaging with your ${t.items}`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="3D tour views" value={d.total_views} testid="stat-views" />
        <Stat label="Avg time in tour" value={mins} testid="stat-avg-time" />
        <Stat label="Leads" value={d.leads} testid="stat-leads" />
        <Stat label="Conversion" value={`${d.conversion_rate}%`} testid="stat-conversion" />
      </div>
      {(!sells || d.booking_clicks.tours.length > 0) && <BookingClicks b={d.booking_clicks} t={t} />}
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        {d.booking_clicks.by_button.length > 0 && <Chart title="Booking clicks by button" data={d.booking_clicks.by_button} dataKey="clicks" testid="chart-booking-buttons" />}
        <Chart title={`Views per ${t.item}`} data={d.views_by_house_type} dataKey="views" testid="chart-views" />
        <Chart title={`Avg seconds in tour by ${t.item}`} data={d.views_by_house_type} dataKey="avg_seconds" testid="chart-time" />
        <Chart title="Most-clicked hotspots" data={d.top_hotspots} dataKey="clicks" testid="chart-hotspots" />
        {sells && <Chart title="Most-chosen upgrades" data={d.top_upgrades} dataKey="picks" testid="chart-upgrades" />}
      </div>
    </div>
  );
}
